import React, { useState, useEffect, useRef, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

interface CustomTooltipProps {
  content: ReactNode;
  targetSelector: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Widest the tooltip may grow before its content wraps. Defaults to 280px. */
  maxWidth?: string;
}

const TooltipContainer = styled.div<{ top: number; left: number; visible: boolean; actualWidth?: number; maxWidth?: string }>`
  position: fixed;
  top: ${({ top }) => top}px;
  left: ${({ left }) => left}px;
  background-color: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
  line-height: 1.4;
  z-index: 10001;
  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.15s ease-in-out, visibility 0.15s ease-in-out;
  font-family: 'DM Sans', sans-serif;
  max-width: ${({ maxWidth }) => maxWidth ?? '280px'};
  /* Tooltips carry free text — a file name, an expedition name — which can be one long unbroken word.
     Wrapping at spaces alone leaves such a word sticking out past max-width, so let it break mid-word
     as a last resort. Text that has spaces to wrap at is unaffected. */
  overflow-wrap: anywhere;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  ${({ actualWidth }) => actualWidth && `width: ${actualWidth}px;`}
`;

// The measured width gets pinned as an explicit `width`. offsetWidth rounds to the nearest pixel, so a 245.6px tooltip pinned at 245px
// is a fraction too narrow and its last letter/word wraps to a second line — measure the fractional width and round *up*.
const measureWidth = (node: HTMLElement) => Math.ceil(node.getBoundingClientRect().width);

const CustomTooltip: React.FC<CustomTooltipProps> = ({ content, targetSelector, position = 'bottom', maxWidth }) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [tooltipActualWidth, setTooltipActualWidth] = useState<number | undefined>(undefined);
  const targetElementRef = useRef<Element | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const portalRootRef = useRef<HTMLElement | null>(null);
  const animationFrameIdRef = useRef<number | null>(null);

  useEffect(() => {
    portalRootRef.current = document.body;
  }, []);

  useEffect(() => {
    const target = document.querySelector(targetSelector);
    targetElementRef.current = target;

    if (!target) {
      return;
    }

    const calculateAndSetPosition = () => {
      if (!targetElementRef.current || !tooltipRef.current) return;

      const targetRect = targetElementRef.current.getBoundingClientRect();
      // Skip positioning when the target is fully outside the viewport — otherwise the
      // downstream clamp pins the tooltip to the top-left corner of the page.
      if (
        targetRect.bottom <= 0 ||
        targetRect.right <= 0 ||
        targetRect.top >= window.innerHeight ||
        targetRect.left >= window.innerWidth
      ) {
        setVisible(false);
        return;
      }
      const tooltipNode = tooltipRef.current;
      
      if (!tooltipActualWidth) {
        setTooltipActualWidth(measureWidth(tooltipNode));
      }
      const currentTooltipWidth = measureWidth(tooltipNode);
      const currentTooltipHeight = tooltipNode.offsetHeight;

      let newTop = 0;
      let newLeft = 0;
      const offset = 8;

      switch (position) {
        case 'top':
          newTop = targetRect.top - currentTooltipHeight - offset;
          newLeft = targetRect.left + (targetRect.width / 2) - (currentTooltipWidth / 2);
          break;
        case 'bottom':
          newTop = targetRect.bottom + offset;
          newLeft = targetRect.left + (targetRect.width / 2) - (currentTooltipWidth / 2);
          break;
        case 'left':
          newTop = targetRect.top + (targetRect.height / 2) - (currentTooltipHeight / 2);
          newLeft = targetRect.left - currentTooltipWidth - offset;
          break;
        case 'right':
          newTop = targetRect.top + (targetRect.height / 2) - (currentTooltipHeight / 2);
          newLeft = targetRect.right + offset;
          break;
        default: // bottom
          newTop = targetRect.bottom + offset;
          newLeft = targetRect.left + (targetRect.width / 2) - (currentTooltipWidth / 2);
      }

      if (newLeft < offset) newLeft = offset;
      if (newLeft + currentTooltipWidth > window.innerWidth - offset) {
        newLeft = window.innerWidth - currentTooltipWidth - offset;
      }
      if (newTop < offset) newTop = offset;
      if (newTop + currentTooltipHeight > window.innerHeight - offset) {
        if (position === 'bottom' && (targetRect.top - currentTooltipHeight - offset > 0)) {
          newTop = targetRect.top - currentTooltipHeight - offset + window.scrollY;
        } else {
          newTop = window.innerHeight - currentTooltipHeight - offset;
        }
      }
      if (position === 'top' && newTop < offset) {
        if (targetRect.bottom + currentTooltipHeight + offset < window.innerHeight) {
          newTop = targetRect.bottom + offset + window.scrollY;
        }
      }

      setCoords({ top: newTop, left: newLeft });
    };
    
    // Reposition listeners are only attached while the tooltip is visible. Keeping them
    // always-on means every tooltip on the page recalculates its position on every scroll
    // event (hundreds of getBoundingClientRect/layout reads), which janks scrolling.
    const addRepositionListeners = () => {
      window.addEventListener('resize', calculateAndSetPosition);
      window.addEventListener('scroll', calculateAndSetPosition, true);
    };
    const removeRepositionListeners = () => {
      window.removeEventListener('resize', calculateAndSetPosition);
      window.removeEventListener('scroll', calculateAndSetPosition, true);
    };

    const showTooltip = () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }
      animationFrameIdRef.current = requestAnimationFrame(() => {
        calculateAndSetPosition();
        setVisible(true);
      });
      addRepositionListeners();
    };

    const hideTooltip = () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
        animationFrameIdRef.current = null;
      }
      setVisible(false);
      setTooltipActualWidth(undefined);
      removeRepositionListeners();
    };

    target.addEventListener('mouseenter', showTooltip);
    target.addEventListener('mouseleave', hideTooltip);
    target.addEventListener('focus', showTooltip);
    target.addEventListener('blur', hideTooltip);

    return () => {
      target.removeEventListener('mouseenter', showTooltip);
      target.removeEventListener('mouseleave', hideTooltip);
      target.removeEventListener('focus', showTooltip);
      target.removeEventListener('blur', hideTooltip);
      window.removeEventListener('resize', calculateAndSetPosition);
      window.removeEventListener('scroll', calculateAndSetPosition, true);
      setVisible(false);
      setTooltipActualWidth(undefined);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetSelector, position]);

  useEffect(() => {
    if (visible && targetElementRef.current && tooltipRef.current) {
      const target = targetElementRef.current;
      const targetRect = target.getBoundingClientRect();
      const tooltipNode = tooltipRef.current;
      setTooltipActualWidth(measureWidth(tooltipNode));
      const currentTooltipWidth = measureWidth(tooltipNode);
      const currentTooltipHeight = tooltipNode.offsetHeight;

      let newTop = 0;
      let newLeft = 0;
      const offset = 8;

      switch (position) {
        case 'top':
          newTop = targetRect.top - currentTooltipHeight - offset;
          newLeft = targetRect.left + (targetRect.width / 2) - (currentTooltipWidth / 2);
          break;
        case 'bottom':
          newTop = targetRect.bottom + offset;
          newLeft = targetRect.left + (targetRect.width / 2) - (currentTooltipWidth / 2);
          break;
        default:
          newTop = targetRect.bottom + offset + window.scrollY;
          newLeft = targetRect.left + (targetRect.width / 2) - (currentTooltipWidth / 2) + window.scrollX;
      }
      
      if (newLeft < offset) newLeft = offset;
      if (newLeft + currentTooltipWidth > window.innerWidth - offset) {
        newLeft = window.innerWidth - currentTooltipWidth - offset;
      }
      if (newTop < offset) newTop = offset;
      if (newTop + currentTooltipHeight > window.innerHeight - offset) {
        newTop = window.innerHeight - currentTooltipHeight - offset;
      }

      setCoords({ top: newTop, left: newLeft });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [content, visible]); 
  if (!portalRootRef.current) {
    return null;
  }

  return ReactDOM.createPortal(
    <TooltipContainer ref={tooltipRef} top={coords.top} left={coords.left} visible={visible} actualWidth={tooltipActualWidth} maxWidth={maxWidth}>
      {content}
    </TooltipContainer>,
    portalRootRef.current
  );
};

export default CustomTooltip; 